import { useEffect, useState } from "react";

const SAMPLE = "The rain arrived quietly in the afternoon, tapping softly against the window as the light outside faded to a soft grey.";
const TYPING_SPEED_MS = 55;   // ~180 char/s
const HOLD_AFTER_MS = 1600;
const CURSOR_BLINK_MS = 500;

/** Animated "demo" of the typing UI. Cycles: types the passage, shows a
 *  live stats bar, holds, resets. Pure CSS/JS, no video asset needed. */
export default function LandingDemo() {
  const [typed, setTyped] = useState("");
  const [cursorOn, setCursorOn] = useState(true);
  const [phase, setPhase] = useState("typing"); // typing | done | reset

  // Typing loop
  useEffect(() => {
    let t;
    if (phase === "typing" && typed.length < SAMPLE.length) {
      t = setTimeout(() => setTyped(SAMPLE.slice(0, typed.length + 1)), TYPING_SPEED_MS);
    } else if (phase === "typing" && typed.length === SAMPLE.length) {
      setPhase("done");
      t = setTimeout(() => setPhase("reset"), HOLD_AFTER_MS);
    } else if (phase === "reset") {
      setTyped("");
      setPhase("typing");
    }
    return () => clearTimeout(t);
  }, [typed, phase]);

  // Cursor blink
  useEffect(() => {
    const t = setInterval(() => setCursorOn((c) => !c), CURSOR_BLINK_MS);
    return () => clearInterval(t);
  }, []);

  // Live stats
  const correctChars = typed.split("").filter((c, i) => c === SAMPLE[i]).length;
  const accuracy = typed.length > 0 ? Math.round((correctChars / typed.length) * 100) : 0;
  const words = typed.trim().split(/\s+/).filter(Boolean).length;
  const wpm = Math.min(120, Math.round(words * 12));  // fictional but plausible

  const rendered = SAMPLE.split("").map((ch, i) => {
    if (i < typed.length) {
      const ok = typed[i] === ch;
      return (
        <span key={i} className={ok ? "text-brand-emerald" : "text-brand-crimson underline"}>
          {ok ? ch : typed[i]}
        </span>
      );
    }
    if (i === typed.length && phase === "typing") {
      return (
        <span key={i} className="relative">
          <span className={`absolute -left-[1px] top-0 bottom-0 w-[2px] bg-brand-gold ${cursorOn ? "opacity-100" : "opacity-0"} transition-opacity`} />
          <span className="text-muted-foreground">{ch}</span>
        </span>
      );
    }
    return <span key={i} className="text-muted-foreground/50">{ch}</span>;
  });

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
      <div className="text-center mb-10 max-w-3xl mx-auto">
        <div className="inline-flex items-center gap-2 px-3 py-1 border border-brand-gold/40 bg-brand-gold/5 rounded-full text-[11px] uppercase tracking-[0.3em] font-bold text-brand-gold mb-6">
          Live demo · watch it type itself
        </div>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-tight">
          Word-by-word accuracy. <span className="text-brand-gold">In real time.</span>
        </h2>
        <p className="mt-3 text-muted-foreground">Green means correct. Red is a miss. Your live WPM and accuracy tick up as you type.</p>
      </div>

      <div className="border border-brand-gold/30 rounded-2xl overflow-hidden bg-card glow-gold" data-testid="landing-demo">
        {/* Fake IDE-style top bar */}
        <div className="flex items-center gap-2 px-4 py-2.5 bg-secondary/60 border-b border-border">
          <div className="w-2.5 h-2.5 rounded-full bg-brand-crimson/70" />
          <div className="w-2.5 h-2.5 rounded-full bg-brand-gold/70" />
          <div className="w-2.5 h-2.5 rounded-full bg-brand-emerald/70" />
          <div className="ml-4 text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Practice · English typing · The Rainy Afternoon</div>
        </div>

        {/* Stats bar */}
        <div className="grid grid-cols-3 divide-x divide-border border-b border-border">
          <div className="p-4 sm:p-5">
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-muted-foreground mb-1">Live WPM</div>
            <div className="font-mono text-3xl sm:text-4xl font-black text-brand-gold">{wpm}</div>
          </div>
          <div className="p-4 sm:p-5">
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-muted-foreground mb-1">Accuracy</div>
            <div className="font-mono text-3xl sm:text-4xl font-black text-brand-emerald">{accuracy}<span className="text-lg">%</span></div>
          </div>
          <div className="p-4 sm:p-5">
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-muted-foreground mb-1">Words</div>
            <div className="font-mono text-3xl sm:text-4xl font-black text-brand-blue">{words}</div>
          </div>
        </div>

        {/* Text area with live highlighter */}
        <div className="p-6 sm:p-10 font-mono text-base sm:text-lg leading-loose min-h-[180px]">
          {rendered}
        </div>
      </div>
    </section>
  );
}
